import {selPropGen} from 'module/Properties/selector/selPropGen'
import {PropID} from 'module/Properties/enum/PropID'
import _ from 'lodash'
import {SisName} from 'util/SisName'
import {Booking, Persurl} from 'generated/JavaTOmodifications'
import {useSelector} from 'react-redux'
import {selEntryDescById} from 'module/edesc/selector/EntryDescSelector'
import {selPersonById} from 'module/Person/selector/PersonSelector'
import {isEqualCFModel} from 'module/EventDialog/util/isEqualCFModel'
import {WorkflowChangeableFields} from 'module/BSEntry/EntryTypeParams'
import {Perm, TypePerm} from 'module/edesc/TypePerm'
import {selCurrentUserlevel, selCurrentUsername} from 'module/SessionInfo/selector/SessionInfoSelector'
import {propDefaultTrueUserlevelGE} from 'module/Properties/util/PropertiesUtil'
import {BSApiAction, BSApiActionLocaleDict} from 'module/BSEntry/util/BSApiAction'
import {stripHTML} from 'util/StringUtil'

const selPrintable = selPropGen(PropID.eventDialog_print, eventDialog_print => {
	const print = _.split(eventDialog_print, ',')
	return {
		[SisName.Persurl]: print.includes('persurl'),
		[SisName.Booking]: print.includes('buchung'),
	} as Record<SisName, boolean>
})
const selOverrideShowDeclineButton = selPropGen(PropID.overrideShowDeclineButton, propDefaultTrueUserlevelGE, selCurrentUserlevel)

export function useBSModelViewPerm(model: Partial<Booking | Persurl>, persistedModel?: Booking | Persurl | null) {
	const EntryDescById = useSelector(selEntryDescById)
	const PersonById = useSelector(selPersonById)
	const printable = useSelector(selPrintable)
	const overrideShowDeclineButton = useSelector(selOverrideShowDeclineButton)
	const oldED = EntryDescById[persistedModel?._edId ?? NaN]
	const oldPerm = oldED && oldED.perm
	const newED = EntryDescById[model?._edId ?? NaN]
	const newPerm = !persistedModel && newED && newED.perm

	const mostlyUnchanged = model && persistedModel && isEqualCFModel(
		_.omit(model, WorkflowChangeableFields),
		_.omit(persistedModel, WorkflowChangeableFields),
	)
	const person = PersonById[model?.persNr ?? 0]
	const selfadmin = !person || person.myself
	const saveNew = newPerm && TypePerm.check(newPerm, Perm.crt, selfadmin)
	const saveChanges = oldPerm && TypePerm.check(oldPerm, Perm.upd, selfadmin)
	const sisName: SisName | undefined = model?.sisName
	const gentext = newED?.gentext || BSApiActionLocaleDict[BSApiAction.GEN]
	const abltext = newED?.abltext || BSApiActionLocaleDict[BSApiAction.ABL]
	const stornos = persistedModel?.stornos || []
	const stornoOpen = !!stornos.length && stornos.some(s => s.answer == null)

	const permDel = TypePerm.check(oldPerm, Perm.del, selfadmin)
	const permAbl = TypePerm.check(oldPerm, Perm.abl, selfadmin)
	const permStorno = TypePerm.check(oldPerm, Perm.storno, selfadmin)

	const hasChanged = !isEqualCFModel(model, persistedModel)
	const showReset = persistedModel && hasChanged
	const showPrint = persistedModel && !showReset && sisName && printable[sisName]
	const showSave = hasChanged && (saveNew || saveChanges)
	const currentUsername = useSelector(selCurrentUsername)
	const stornoUserAnswers = _.filter(stornos, s => s.user === currentUsername)
	const stornoAnswered = stornoUserAnswers.length && _.every(stornoUserAnswers, s => s.answer != null)
	const showStorno = !hasChanged && !stornoAnswered && (
		// always show storno button when there are requests (and del or storno permission)
		stornoOpen && (permDel || permAbl || permStorno)
		||
		// or if there are no existing storno, then show only if user has no del permission
		!stornoOpen && permStorno && !(permDel || permAbl)
	)
	// Delete button is not required, when there is a "delete request".
	const showDelete = !showStorno && mostlyUnchanged && persistedModel && permDel
	const showGen =
		// gen existing entry
		TypePerm.check(oldPerm, Perm.gen, selfadmin) && (mostlyUnchanged || TypePerm.check(oldPerm, Perm.upd, selfadmin))
		||
		// gen and create of new entry
		newPerm && TypePerm.check(newPerm, Perm.gen, selfadmin) && TypePerm.check(newPerm, Perm.crt, selfadmin)
	const showAbl = !showStorno && overrideShowDeclineButton && mostlyUnchanged && permAbl

	const gentextPlain = stripHTML(gentext)
	const abltextPlain = stripHTML(abltext)
	return {
		stornos, stornoOpen,
		gentext, abltext, gentextPlain, abltextPlain,
		showPrint, showSave, showStorno, showDelete, showGen, showAbl, showReset,
		hasChanged,
	}
}